// src/pages/VoxDashboard.jsx
import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar as CalendarIcon } from "lucide-react";

import DailyBriefingPanel from "@/components/DailyBriefingPanel";
import CalendarPanel from "@/components/CalendarPanel";
import ChatPanel from "@/components/ChatPanel";
import TasksPanel from "@/components/TasksPanel";

import { useChatAssistant } from "@/hooks/useChatAssistant";
import { Toaster } from "@/components/ui/toaster";

/**
 * Backend base URL
 */
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://api.voxconsole.com";

axios.defaults.baseURL = API_BASE;
axios.defaults.withCredentials = true;

const safeArray = (v) => (Array.isArray(v) ? v : []);

export default function VoxDashboard() {
  const [tasks, setTasks] = useState([]);
  const [memories, setMemories] = useState([]);

  const [displayName, setDisplayName] = useState(
    () =>
      localStorage.getItem("vox.displayName") ||
      localStorage.getItem("vox.userName") ||
      "John"
  );

  useEffect(() => {
    fetchTasks();
    fetchMemories();
    fetchUserDisplayName();
  }, []);

  // ----------------------
  // Data fetchers
  // ----------------------
  const fetchTasks = async () => {
    try {
      const res = await axios.get("/api/tasks");
      setTasks(safeArray(res.data));
    } catch {}
  };

  const fetchMemories = async () => {
    try {
      const res = await axios.get("/api/memories");
      setMemories(safeArray(res.data));
    } catch {}
  };

  const fetchUserDisplayName = async () => {
    const candidates = ["/api/user", "/api/me", "/api/profile", "/api/auth/me"];
    for (const path of candidates) {
      try {
        const res = await axios.get(path);
        const u = res?.data || {};
        const value =
          u.display_name ||
          u.displayName ||
          u.name ||
          u.full_name ||
          u.fullName ||
          u.user?.display_name ||
          u.user?.name ||
          u.username ||
          u.user?.username;

        if (value) {
          setDisplayName(value);
          try {
            localStorage.setItem("vox.displayName", value);
          } catch {}
          return;
        }
      } catch {}
    }
  };

  // ----------------------
  // Chat
  // ----------------------
  const chat = useChatAssistant({
    displayName,
    fetchTasks,
    fetchMemories,
    setTasks,
    setMemories,
  });

  return (
    <>
      <Toaster />

      {/* 🧠 MAIN DASHBOARD */}
      <div className="min-h-screen w-full p-4 pt-20">
        <div className="grid grid-cols-12 gap-4">
          {/* DAILY BRIEFING */}
          <div
            className="col-span-12 xl:col-span-4 console-card glow-variant-a"
            style={{ padding: 0 }}
          >
            <DailyBriefingPanel />
          </div>

          {/* CALENDAR */}
          <div
            className="col-span-12 xl:col-span-4 xl:row-span-2 console-card glow-variant-b"
            style={{ padding: 0 }}
          >
            <div className="h-full p-4">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="w-5 h-5 text-neon-cyan" />
                <span className="console-card-title">Calendar</span>
              </div>
              <CalendarPanel />
            </div>
          </div>

          {/* CHAT */}
          <div className="col-span-12 xl:col-span-4 xl:row-span-3 glow-variant-c">
            <ChatPanel
              messages={chat.messages}
              onSendMessage={chat.sendMessage}
              onClearChat={chat.clearChat}
            />
          </div>

          {/* TASKS */}
          <div
            className="col-span-12 xl:col-span-4 console-card glow-variant-d"
            style={{ padding: 0 }}
          >
            <TasksPanel />
          </div>
        </div>
      </div>
    </>
  );
}
